import * as Sentry from '@sentry/react';
import { FirebaseError } from '@firebase/util';
import { doc, onSnapshot, type DocumentReference, type DocumentData } from 'firebase/firestore';
import { db } from '../../config';
import type { SystemConfigType } from '../../../../types/system';

// システム設定はコレクション「system」のドキュメント「config」で一元管理する
const SYSTEM_COLLECTION = 'system';
const SYSTEM_CONFIG_DOC = 'config';

const systemConfigDocument = (): DocumentReference<DocumentData> =>
  doc(db, SYSTEM_COLLECTION, SYSTEM_CONFIG_DOC);

/**
 * システム設定（メンテナンスフラグ等）をリアルタイム監視する。
 * ドキュメントが存在しない場合はメンテナンス無効として扱う。
 * @param callback - 設定更新時に呼ばれるコールバック
 * @returns リスナー解除関数
 */
export const subscribeSystemConfig = (
  callback: (config: SystemConfigType) => void,
): (() => void) => {
  const unsubscribe = onSnapshot(
    systemConfigDocument(),
    snapshot => {
      if (!snapshot.exists()) {
        callback({ maintenance_enabled: false });
        return;
      }
      const data = snapshot.data();
      callback({
        maintenance_enabled: data.maintenance_enabled === true,
        maintenance_message:
          typeof data.maintenance_message === 'string' ? data.maintenance_message : undefined,
      });
    },
    error => {
      // 取得失敗時はメンテ無効フォールバックで通知する。
      // これを呼ばないと購読側の初回取得フラグが立たず、
      // 保護ルートがローディングのまま固まる（#2879 退行防止）。
      // onSnapshot は error 発火でリスナーが終了するため、ここで確定させる必要がある。
      // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作。
      if (error instanceof FirebaseError && error.code === 'permission-denied') {
        callback({ maintenance_enabled: false });
        return;
      }
      Sentry.captureException(error);
      callback({ maintenance_enabled: false });
    },
  );

  return unsubscribe;
};
